import React, { useCallback, useMemo, useState } from 'react';
import cx from 'classnames';
import { useRouter } from 'next/router';
import { useAnalytics } from '@/analytics/AnalyticsProvider';
import { useMeasure } from 'react-use';
import {
    CheckoutError,
    EducatorSelected,
    NewParentAccount,
    ParentSelected,
    StudentSelected,
    TimesTablesPaidParent,
    UserTypeSelected,
    UserTypeSubmitted
} from '@/analytics/events';
import RedirectModal from '@/components/RedirectModal/RedirectModal';
import { submitDuplicateAccountCheck } from '@/lib/checkout_helpers/submitDuplicateAccountCheck';
import { writeToLocalStorage } from '@/components/Checkout/utils/CheckoutProcess';
import * as Events from '@/analytics/events';

/* eslint-disable camelcase */
const userTypes = [
    {
        name: 'Student',
        svg: (
            <svg width='66' height='70' viewBox='0 0 66 70' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M47.4995 0.865234C49.3425 0.865234 50.8615 2.19014 51.0691 3.89703L51.0933 4.29728V7.72933L51.8107 7.74613C59.1788 8.092 65.0885 13.7358 65.4507 20.7723L65.4683 21.4575V52.346C65.4683 58.5493 61.1599 63.7917 55.245 65.4929L54.687 65.6383V66.0742C54.687 67.8342 53.2997 69.2849 51.5124 69.4831L51.0933 69.5062H15.1558C13.3128 69.5062 11.7938 68.1813 11.5862 66.4744L11.562 66.0742V65.6383L11.0041 65.4929C5.30817 63.8547 1.1021 58.9328 0.798354 53.0311L0.780762 52.346V21.4575C0.780762 14.1054 6.83252 8.10315 14.4383 7.74613L15.1558 7.72933V4.29728C15.1558 2.53721 16.5431 1.08658 18.3304 0.888324L18.7495 0.865234H47.4995ZM43.9057 7.72912H22.3432V11.1612C22.3432 12.9212 20.9558 14.3719 19.1685 14.5701L18.7494 14.5932H15.1556C11.3665 14.5932 8.26224 17.3933 7.98786 20.945L7.96815 21.4573V52.3457C7.96815 55.9644 10.9002 58.929 14.6192 59.191L15.1556 59.2098C16.9987 59.2098 18.5176 60.5347 18.7252 62.2416L18.7494 62.6419H47.4994C47.4994 60.8818 48.8867 59.4312 50.674 59.2329L51.0932 59.2098C54.8823 59.2098 57.9866 56.4097 58.2609 52.858L58.2807 52.3457V21.4573C58.2807 17.8387 55.3486 14.8741 51.6296 14.612L51.0932 14.5932H47.4994C45.6564 14.5932 44.1374 13.2683 43.9298 11.5614L43.9057 11.1612V7.72912ZM27.7701 31.7537C27.7701 29.8582 26.1611 28.3216 24.1763 28.3216L23.7213 28.3447C21.9339 28.543 20.5466 29.9936 20.5466 31.7537C20.5466 33.6491 22.1556 35.1857 24.1404 35.1857L24.5954 35.1626C26.3827 34.9644 27.7701 33.5138 27.7701 31.7537ZM45.7389 31.7537C45.7389 29.8582 44.1299 28.3216 42.1451 28.3216L41.6901 28.3447C39.9028 28.543 38.5154 29.9936 38.5154 31.7537C38.5154 33.6491 40.1244 35.1857 42.1092 35.1857L42.5642 35.1626C44.3516 34.9644 45.7389 33.5138 45.7389 31.7537ZM39.5422 43.0795C40.9312 41.7256 43.2065 41.7034 44.6242 43.03C46.0419 44.3565 46.0651 46.5295 44.6761 47.8834C41.6343 50.8482 37.471 52.5189 33.1247 52.5189C28.7785 52.5189 24.6152 50.8482 21.5734 47.8834C20.1843 46.5295 20.2076 44.3565 21.6253 43.03C23.043 41.7034 25.3183 41.7256 26.7073 43.0795C28.3972 44.7266 30.7101 45.6548 33.1247 45.6548C35.5393 45.6548 37.8523 44.7266 39.5422 43.0795Z'
                    fill='#2E2D2D'
                />
            </svg>
        ),
        svg_mobile: (
            <svg width='49' height='52' viewBox='0 0 49 52' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M35.3333 0.172852C36.7223 0.172852 37.867 1.17133 38.0234 2.45769L38.0417 2.75932V5.34579L38.5824 5.35845C44.1351 5.61911 48.5888 9.87239 48.8617 15.1753L48.875 15.6917V38.9699C48.875 43.6449 45.6281 47.5957 41.1705 48.8778L40.75 48.9873V49.3158C40.75 50.6422 39.7045 51.7355 38.3575 51.8849L38.0417 51.9023H10.9583C9.5694 51.9023 8.42467 50.9038 8.26822 49.6174L8.25 49.3158V48.9873L7.82952 48.8778C3.53696 47.6432 0.367169 43.9339 0.138258 39.4863L0.125 38.9699V15.6917C0.125 10.151 4.68575 5.62751 10.4176 5.35845L10.9583 5.34579V2.75932C10.9583 1.43289 12.0039 0.33966 13.3508 0.190253L13.6667 0.172852H35.3333ZM32.6252 5.34559H16.3752V7.93206C16.3752 9.2585 15.3297 10.3517 13.9827 10.5011L13.6669 10.5185H10.9585C8.10296 10.5185 5.7635 12.6288 5.55672 15.3054L5.54186 15.6915V38.9697C5.54186 41.6968 7.75153 43.931 10.5543 44.1285L10.9585 44.1427C12.3475 44.1427 13.4922 45.1411 13.6486 46.4275L13.6669 46.7291H35.3335C35.3335 45.4027 36.3791 44.3095 37.726 44.1601L38.0419 44.1427C40.8974 44.1427 43.2369 42.0324 43.4437 39.3558L43.4585 38.9697V15.6915C43.4585 12.9644 41.2489 10.7302 38.4461 10.5327L38.0419 10.5185H35.3335C33.9446 10.5185 32.7999 9.52005 32.6434 8.2337L32.6252 7.93206V5.34559ZM20.4646 23.4512C20.4646 22.0227 19.2521 20.8647 17.7563 20.8647L17.4134 20.8821C16.0664 21.0315 15.0209 22.1248 15.0209 23.4512C15.0209 24.8797 16.2334 26.0377 17.7292 26.0377L18.0721 26.0203C19.4191 25.8709 20.4646 24.7776 20.4646 23.4512ZM34.0065 23.4512C34.0065 22.0227 32.7939 20.8647 31.2981 20.8647L30.9552 20.8821C29.6083 21.0315 28.5627 22.1248 28.5627 23.4512C28.5627 24.8797 29.7753 26.0377 31.2711 26.0377L31.614 26.0203C32.961 25.8709 34.0065 24.7776 34.0065 23.4512ZM29.3363 31.9866C30.3832 30.9663 32.0979 30.9496 33.1663 31.9493C34.2347 32.949 34.2522 34.5866 33.2054 35.6069C30.9131 37.8413 27.7755 39.1004 24.5 39.1004C21.2246 39.1004 18.087 37.8413 15.7947 35.6069C14.7479 34.5866 14.7654 32.949 15.8338 31.9493C16.9022 30.9496 18.6169 30.9663 19.6637 31.9866C20.9372 33.2279 22.6803 33.9274 24.5 33.9274C26.3197 33.9274 28.0628 33.2279 29.3363 31.9866Z'
                    fill='#2E2D2D'
                />
            </svg>
        )
    },
    {
        name: 'Parent',
        svg: (
            <svg className='w-73 h-70' viewBox='0 0 73 63' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M36.0074 5.87122L36.1398 5.9861L36.263 5.88083C40.8804 2.01005 47.0687 0.165793 53.2707 0.937775L54.1566 1.06592C62.1278 2.38054 68.6579 7.84523 71.0694 15.2191C73.4808 22.593 71.3678 30.6356 65.5968 36.0489L65.4253 36.1881L38.6543 61.5115C37.3615 62.7343 35.3393 62.8284 33.9342 61.7937L33.5966 61.5115L6.62794 36.0028L6.01126 35.4042C0.691434 30.0148 -1.20333 22.2835 1.1136 15.1672C3.52321 7.76633 10.08 2.28434 18.0802 0.981898C24.5988 -0.0793522 31.176 1.78077 36.0074 5.87122ZM11.7008 31.1414L36.125 54.2408L60.5493 31.1414L60.6883 31.0195L61.1186 30.5977C64.5405 27.008 65.7355 21.9356 64.2083 17.2655C62.6006 12.3495 58.2472 8.7064 52.9331 7.82999C47.619 6.95357 42.2383 8.99131 38.9974 13.1076C37.5548 14.9399 34.6727 14.9323 33.2407 13.0923C30.0133 8.94533 24.6211 6.88017 19.2876 7.74847C13.9542 8.61677 9.58299 12.2714 7.97659 17.2054C6.4505 21.8926 7.66523 26.9801 11.115 30.5666L11.7008 31.1414Z'
                    fill='#2E2D2D'
                />
            </svg>
        ),
        svg_mobile: (
            <svg className='w-[55px] h-[52px]' viewBox='0 0 55 47' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M27.4109 4.22175L27.5107 4.30833L27.6035 4.22899C31.0833 1.31189 35.747 -0.0779884 40.4209 0.503795L41.0886 0.600368C47.0958 1.5911 52.0171 5.70941 53.8344 11.2666C55.6517 16.8237 54.0593 22.8847 49.7102 26.9643L49.5809 27.0693L29.4057 46.1536C28.4313 47.0751 26.9074 47.146 25.8485 46.3662L25.594 46.1536L5.26985 26.9296L4.80511 26.4785C0.795961 22.4169 -0.631978 16.5904 1.11412 11.2274C2.93006 5.64996 7.87143 1.5186 13.9005 0.537047C18.8131 -0.262736 23.7699 1.1391 27.4109 4.22175ZM9.09341 23.266L27.5001 40.6742L45.9068 23.266L46.0115 23.1742L46.3358 22.8563C48.9147 20.151 49.8152 16.3283 48.6642 12.8088C47.4527 9.104 44.1719 6.35845 40.167 5.69797C36.1622 5.03748 32.1072 6.57317 29.6648 9.6753C28.5775 11.0562 26.4055 11.0504 25.3264 9.66377C22.8941 6.53851 18.8304 4.98217 14.811 5.63653C10.7916 6.2909 7.49738 9.04514 6.28675 12.7635C5.13666 16.2959 6.05211 20.1299 8.65194 22.8328L9.09341 23.266Z'
                    fill='#2E2D2D'
                />
            </svg>
        )
    },
    {
        name: 'Educator',
        svg: (
            <svg width='58' height='70' viewBox='0 0 58 70' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M47.0703 3.89703C46.8627 2.19014 45.3437 0.865234 43.5007 0.865234H36.3132L35.6797 0.882713C30.0202 1.19587 25.5319 5.67806 25.5319 11.1614V14.8058C11.3505 16.4952 0.376953 28.0486 0.376953 42.0496C0.376953 57.2133 13.2488 69.506 29.127 69.506C45.0051 69.506 57.8769 57.2133 57.8769 42.0496C57.8769 30.9157 50.9375 21.3296 40.9639 17.0209C44.5901 15.3626 47.0944 11.8253 47.0944 7.72933V4.29728L47.0703 3.89703ZM7.56434 42.0494C7.56434 31.8463 15.3345 23.3764 25.5319 21.742V27.7562C24.8747 27.5501 24.214 27.2879 23.5478 26.9696C21.7729 26.1214 19.614 26.8079 18.7258 28.503C17.8377 30.1981 18.5566 32.2599 20.3316 33.1081C26.1343 35.881 32.1201 35.881 37.9228 33.1081C39.6978 32.2599 40.4167 30.1981 39.5286 28.503C38.6404 26.8079 36.4815 26.1214 34.7066 26.9696C34.0393 27.2884 33.3776 27.5509 32.7194 27.7572V21.7416C42.918 23.3751 50.6893 31.8455 50.6893 42.0494C50.6893 53.4222 41.0355 62.6417 29.1268 62.6417C17.2182 62.6417 7.56434 53.4222 7.56434 42.0494ZM36.3131 7.72912H39.9068L39.8826 8.12937C39.6751 9.83626 38.1561 11.1612 36.3131 11.1612H32.7193L32.7435 10.7609C32.9511 9.05402 34.4701 7.72912 36.3131 7.72912Z'
                    fill='#2E2D2D'
                />
            </svg>
        ),
        svg_mobile: (
            <svg width='45' height='52' viewBox='0 0 45 52' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M36.023 2.45671C35.8666 1.17036 34.7219 0.171875 33.3329 0.171875H27.9163L27.4389 0.185047C23.1738 0.421053 19.7913 3.79893 19.7913 7.93129V10.6784C9.10385 11.9517 0.833984 20.6585 0.833984 31.21C0.833984 42.6377 10.5345 51.9017 22.5007 51.9017C34.4668 51.9017 44.1673 42.6377 44.1673 31.21C44.1673 22.8189 38.9373 15.5945 31.4205 12.3474C34.1536 11.0978 36.0413 8.43183 36.0413 5.34482V2.75835L36.023 2.45671ZM6.25067 31.2097C6.25067 23.5204 12.1063 17.1374 19.7913 15.9056V20.4377C19.296 20.2824 18.798 20.0848 18.296 19.8448C16.9583 19.2056 15.3313 19.723 14.662 21.0005C13.9927 22.278 14.5344 23.8318 15.8721 24.471C20.2452 26.5607 24.7562 26.5607 29.1293 24.471C30.467 23.8318 31.0087 22.278 30.3394 21.0005C29.6701 19.723 28.0431 19.2056 26.7054 19.8448C26.2026 20.0851 25.7039 20.283 25.2079 20.4384V15.9052C32.8939 17.1362 38.7507 23.5197 38.7507 31.2097C38.7507 39.7805 31.4753 46.7285 22.5007 46.7285C13.526 46.7285 6.25067 39.7805 6.25067 31.2097ZM27.9165 5.34486H30.6249L30.6067 5.6465C30.4502 6.93285 29.3055 7.93133 27.9165 7.93133H25.2082L25.2264 7.6297C25.3829 6.34334 26.5276 5.34486 27.9165 5.34486Z'
                    fill='#2E2D2D'
                />
            </svg>
        )
    }
];
/* eslint-enable camelcase */

const CheckoutIntro = ({ isLoading, setLoading, nextStage }) => {
    const analytics = useAnalytics();
    const router = useRouter();
    const { email, token } = router.query ?? {};
    const [redirectModal, setRedirectModal] = useState(null);
    const [inactiveAccount, setInactiveAccount] = useState(false);

    const submitUser = useCallback(
        (userType) => {
            console.log('submitting user type', userType);

            const event = {
                ...router.query,
                userType,
                email,
                token
            };

            //! analytics.track(UserTypeSubmitted, event);

            switch (userType) {
                case 'Student':
                    //! analytics.track(StudentSelected, event);
                    nextStage({ stage: 1 });
                    break;
                case 'Parent': {
                    // begin the checkout session
                    // if (email)
                    //     return router.push(`/parent-checkout?email=${encodeURIComponent(email as string)}`);
                    // else
                    //     return router.push('/parent-checkout');
                    const payload = {
                        ...event,
                        isParentsPage: false,
                        beginCheckout: true
                    };
                    analytics.track(Events.ParentSelected, payload);
                    analytics.track(Events.CheckoutBegins, payload);
    
                    writeToLocalStorage('checkout-state', { pathname: '/checkout', ...router.query, email, token });
                    router.push({
                        pathname: '/checkout', query: {
                            ...router.query, email, token
                        }
                    }, undefined, { shallow: true }).then(() => {
                        analytics.track(Events.NewParentRegister, payload);
                    });
                    break;
                }
                case 'Educator':
                    // analytics.track(EducatorSelected, event);
                    // analytics.track(EducatorSelected, { userType, email, token });
                    // router.push(
                    //     { pathname: '/signup', query: { ...router.query, email, token } },
                    //     undefined,
                    //     { shallow: true }
                    // ).then(() => {
                    //     analytics.track(Events.NewTeacherRegister, event);
                    // });
                    router.push({ pathname: '/signup', query: { ...router.query, email, token } }, undefined, { shallow: true });
                    // analytics.track(EducatorSelected, event, {}, () => {
                    //     analytics.track(Events.NewTeacherRegister, event, {}, () => {
                    //         router.push({ pathname: '/signup', query: { ...router.query, email, token } }, undefined, { shallow: true });
                    //     });
                    // });
                    break;
                default:
                    return null;
            }
        },
        [analytics, email, nextStage, router, setLoading, token]
    );

    const handleSetUserType = useCallback(
        async (userType) => {
            await analytics.track(UserTypeSelected, { userType });
            submitUser(userType);
        },
        [analytics, submitUser]
    );

    const createUsers = useMemo(() => {
        return userTypes.map((user, index) => {
            const isSelected = false; //user.name === userType;
            const classes = cx(
                'w-[286px] sm:w-[195px] md:w-[222px] lg:w-[238px] h-[140px] sm:h-[247px] py-1 border-solid border-[.24px] border-[#BCBCBC] rounded-xl  bg-white cursor-pointer select-none',
                {
                    'bg-checkout-plan-selected shadow-checkout-plan-selected': isSelected,
                    'hover:bg-[#5F42CD] active:bg-[#342274] rounded-xl': !isSelected
                }
            );

            return (
                <button
                    id={user.name}
                    key={user.name}
                    className={classes}
                    style={{ boxShadow: '0px 2.998px 2.998px 0px rgba(112, 108, 108, 0.25)' }}
                    onClick={() => handleSetUserType(user.name)}
                    aria-label={user.name}
                >
                    <div className='bg-white rounded-xl'>
                        <div className='pb-3 sm:pb-11 pt-1 sm:pt-[54px]'>
                            <div className='text-[#444242] text-[28px] sm:text-4xl font-semibold text-center'>{user.name}</div>
                            <div className='hidden md:flex justify-center  mt-5 h-[70px]'>{user.svg}</div>
                            <div className='visible md:hidden '>
                                <div className='flex justify-center  mt-3'>{user.svg_mobile}</div>
                            </div>
                        </div>
                    </div>
                </button>
            );
        });
    }, [handleSetUserType]);

    return (
        <div className='select-none font-Poppins mt-6 sm:mt-12 min-h-[900px] sm:min-h-[100%] bg-checkout-timestables-background sm:bg-none pt-1 sm:pt-0'>
            {redirectModal && <RedirectModal reActivate={inactiveAccount} />}
            <div className='max-w-[1000px] mx-auto text-left sm:pl-5 lg:pl-0'>
                <h1 className='m-0 hidden sm:block text-[44px] font-semibold'>Let's Get Started!</h1>
                <div className='hidden sm:block text-[#4E4E4E] text-xl font-light text-center sm:text-left max-w-[3600px] sm:max-w-full mx-auto sm:mx-0 px-4 sm:px-0'>
                    Learn Smarter. Not Harder. Muzology will Help Make Math Fun & Easy!
                </div>
            </div>
            <div className='flex flex-col sm:flex-row gap-8 sm:gap-3 md:gap-6 lg:gap-10 w-full justify-center items-center mt-16'>{createUsers}</div>
        </div>
    );
};

export default CheckoutIntro;
